import {useTranslation} from 'react-i18next';
import React, {useEffect, useState} from 'react';
import Grid from '@mui/material/Grid';
import config from '../../../../app/config';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import _ from 'lodash';
import moment from 'moment';
import MessageBox from '../../../../util/messageBox';
import {useSelector} from 'react-redux';
import {user} from '../../../../store/reducers/user';
import {ActivityTreatment} from '../../../../controls/agyle/dictionaries';
import API from '../../../../app/api';
import Notes from '../../../../util/notes';
import Formatter from '../../../../util/formatter';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import AgyleDictionaryField from '../../../../components/ui/selector/agyleDictionaryField';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import Typography from '@mui/material/Typography';
import {calculateArrivalDate} from '../../../../util/patientUtil';
import {addOrReplace, getPatientFlagValue} from '../../mainScreen/util/patientFlagsUtil';
import MiniFormDialog from '../../../../components/modal/miniFormDialog';
import {consultantDictionary} from '../../../../controls/agyle/clinician/clinitianUtils';
import ControlConsultantComponent from '../../../../controls/agyle/clinician/controlConsultantComponent';

const useStyles = makeStyles((theme) => ({
    error: {
        color: '#f44336',
    },
    input: {
        width: '100%',
    },
    paper: {
        padding: 10,
    },
    switchRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginLeft: 5,
    },
}));

const CsmContactTime = (props) => {
    const {t} = useTranslation();
    const classesLocal = useStyles();

    // User
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    const controller = new AbortController();

    const [wards, setWards] = useState([]);

    // Data state
    const [form, setForm] = useState({
        by: null,
        login: '',
        when: null,
        bed: null,
        ward: null,
        activityTreatment: null,
        timestamp: null,
    });
    const [sr, setSr] = useState(getPatientFlagValue(props.patient, 'sr'));

    // Message Box opener state
    const [confirmClear, setConfirmClear] = useState(false);

    // Data picker validation
    const [errorPicker, setErrorPicker] = useState(null);

    useEffect(() => {
        let cancel = false;

        getWardFromApi(cancel);

        let data;
        if (props.patient?.csmContactedTime) {
            data = {
                ...props.patient.csmContactedTime,
                when: props.patient.csmContactedTime.when
                    ? moment(props.patient.csmContactedTime.when, config.defaultDateTimeFormat).isValid()
                        ? moment(props.patient.csmContactedTime.when, config.defaultDateTimeFormat) : moment(props.patient.csmContactedTime.when)
                    : null,
            };
            setForm(data);
        }

        if (_.isUndefined(data?.by) || _.isEmpty(data?.by)) {
            API.getDictByURL(`/$dict/Clinicians`, controller.signal).then(dictData => {
                if (dictData) {
                    for (let clinitian of dictData.__data) {
                        if (clinitian.userName === userObject.userName) {
                            setForm(prevState => ({
                                ...prevState,
                                by: clinitian,
                            }));
                            break;
                        }
                    }
                }
            });
        }

        return () => {
            cancel = true;
            controller.abort();
        };
    }, []);

    const getWardFromApi = async (cancel) => {
        await API.getWards(controller.signal).then(response => {
            if (cancel) return;
            if (response?.data?.__data) {
                setWards(_.orderBy(response?.data?.__data, ['wardName'], ['asc']));
            }
        });
    };

    const prepareNote = () => {
        return Notes.getCSMContactTimeNote(form, sr);
    };

    const validation = () => {
        return (form.by && form.when && !errorPicker) || (!form.by && !form.when && !form.ward);
    };

    const submit = () => {
        if (validation() && !errorPicker) {
            let data = _.cloneDeep(form);
            const dateOfForm = moment().toISOString();
            data = {
                ...data,
                when: data.when ? moment(data.when).toISOString() : null,
                timestamp: dateOfForm,
            };

            const objToSave = ([
                {
                    name: 'csmContactedTime',
                    value: data,
                    noteToAdd: prepareNote(),
                },
            ]);

            if (sr !== getPatientFlagValue(props.patient, 'sr')) {
                let flags = [];
                if (props.patient.metadata && props.patient.metadata.patientFlags) {
                    flags = _.cloneDeep(props.patient.metadata.patientFlags);
                }

                const flag = {
                    name: 'sr',
                    checked: sr,
                    by: tester,
                    login: userObject?.userName,
                    date: moment().format('YYYY-MM-DD HH:mm:ss'),
                };
                addOrReplace(flags, flag);
                objToSave.push({name: 'metadata.patientFlags', value: flags});
            }

            if (data.bed === 'req') {
                objToSave.push({
                    name: 'metadata.bedRequest',
                    value: {
                        requested: data.when,
                        ready: null,
                    },
                });
            }

            if (data.bed !== 'req') {
                const bedRequest = props.patient?.metadata.bedRequest || {};
                objToSave.push({
                    name: 'metadata.bedRequest',
                    value: {
                        ...bedRequest,
                        [data.bed]: data.when,
                    },
                });
            }

            props.onChange(objToSave);
        } else {
            return false;
        }
    };

    const clearData = () => {
        setForm({
            by: '',
            login: '',
            when: null,
            bed: null,
            ward: null,
            activityTreatment: null,
        });

        setSaveData(true);
        setConfirmClear(false);
    };

    const [saveData, setSaveData] = React.useState(false);

    return (
        <>
            <MiniFormDialog
                title={t(props._formTitle)}
                open={props.open}
                onClose={props.onClose}
                disableSubmit={!saveData || props.disableSave}
                onSave={submit}
                formOptions={props._formOptions}
                buttons={[
                    <AgyleButton
                        key="delete"
                        disabled={props.disableSave}
                        onClick={() => setConfirmClear(true)}
                    >
                        {t('Delete')}
                    </AgyleButton>,
                ]}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <ControlConsultantComponent
                            allowedConsultants={consultantDictionary.csmContactTime}
                            error={!form.by && (form.when || form.ward)}
                            label="Clinician"
                            agyleRefresh
                            value={form.by}
                            onChange={event => {
                                setForm(prevState => ({...prevState, by: event}));
                                setSaveData(true);
                            }}
                            size="large"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleDatePicker
                            type="dateTime"
                            inputFormat={config.defaultDateTimeFormat}
                            label={t('CSM Contact Date and Time')}
                            error={!form.when && (form.by || form.ward)}
                            value={form.when}
                            minDate={moment(calculateArrivalDate(props.patient))}
                            maxDate={moment()}
                            onError={setErrorPicker}
                            onChange={event => {
                                setForm(prevState => ({...prevState, when: event}));
                                setSaveData(true);
                            }}
                            size="large"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleDictionaryField
                            name={'Wards'}
                            label={t('Wards')}
                            renderInput={(params) => <TextField {...params} label="Ward"/>}
                            getOptionLabel={option => option.wardName}
                            options={wards}
                            value={form.ward}
                            isOptionEqualToValue={(option, value) => option.__id == value.__id}
                            onChange={(event, newValue) => {
                                setForm(prevState => ({...prevState, ward: newValue}));
                                setSaveData(true);
                            }}
                            size="large"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <ActivityTreatment
                            name={'Activity Treatment'}
                            label={t('Activity Treatment')}
                            color="primary"
                            agyleRefresh
                            value={form.activityTreatment}
                            onChange={event => {
                                setForm(prevState => ({
                                    ...prevState,
                                    activityTreatment: event,
                                }));
                                setSaveData(true);
                            }}
                            size="large"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={6} className={classesLocal.switchRow}>
                        <Typography variant="body1" minWidth="100px">
                            {t('Side Room')}
                        </Typography>

                        <AgyleBlockSwitch
                            onChange={(value) => {
                                setSr(value);
                                setSaveData(true);
                            }}
                            value={sr}
                        />
                    </Grid>

                    <Grid item xs={12} className={classesLocal.switchRow}>
                        <Typography variant="body1" minWidth="100px">
                            {t('Bed Request')}
                        </Typography>

                        <AgyleBlockSwitch
                            onChange={(value) => {
                                setForm(prevState => ({...prevState, bed: value}));
                                setSaveData(true);
                            }}
                            value={form.bed}
                            customLabels={{
                                yes: t('Requested'),
                                no: t('Ready'),
                            }}
                            valueObject={{
                                true: 'req',
                                false: 'ready',
                            }}
                        />
                    </Grid>
                </Grid>
            </MiniFormDialog>

            <MessageBox
                title={t('CAUTION')}
                haveAccept={{yes: t('Yes'), no: t('No')}}
                onAccept={clearData}
                onClose={() => setConfirmClear(false)}
                message={t('Are you sure you want delete all recorded values?')}
                open={confirmClear}
                warning
            />
        </>
    );
};

export default CsmContactTime;
